import { supabase } from "@/supabase";
import * as tus from "tus-js-client";
import { storage_url } from "@/utils/general";

const _BUCKET = "brandripplr";
// Private bucket for personal documents (creator licenses: Emirates ID, phone, address).
// Read through short-lived signed URLs only — see 20261022290000_private_license_bucket.sql.
export const PRIVATE_BUCKET = "brandripplr-private";

// Finance documents and talent contracts also live in the private bucket,
// under the same paths they always had (20261029170000_private_finance_docs.sql):
//   campaign/<id>/booking-orders/             PO/BO, quotation, invoice, SOW, credit note
//   campaign/<id>/invitation/<id>/contracts/  talent contract, counter contract, briefing
//   campaign/<id>/contracts/                  older talent contracts
//   campaign/<id>/influencer-vat-invoices/    influencer VAT invoices
// Uploads to these folders go to the private bucket automatically, and links
// open through 5-minute signed URLs (StorageService.openFile / fileUrl).
const PRIVATE_DOC_RE = /^campaign\/\d+\/(booking-orders|contracts|influencer-vat-invoices|invitation\/\d+\/contracts)\//;

/** A stored file's path, from either a path or one of our storage URLs. */
export function toStoragePath(value) {
  let s = String(value || "").trim();
  if (s.startsWith(storage_url)) {
    s = s.slice(storage_url.length);
  } else {
    const m = s.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/brandripplr(?:-private)?\/([^?#]+)/);
    if (m) s = decodeURIComponent(m[1]);
    else if (/^https?:\/\//i.test(s)) return s; // not one of ours
  }
  // keep "//": older contracts were stored under ".../contracts//<file>"
  return s.split(/[?#]/)[0].replace(/^\/+/, "");
}

export function isPrivateDocPath(value) {
  return PRIVATE_DOC_RE.test(toStoragePath(value));
}

function bucketFor(path) {
  return isPrivateDocPath(path) ? PRIVATE_BUCKET : _BUCKET;
}

function isNotFound(error) {
  return !!error && (String(error.statusCode) === "404" || /not.?found/i.test(String(error.message || "")));
}

export const StorageService = {
  async download(path) {
    return await supabase.storage.from(bucketFor(path)).download(toStoragePath(path));
  },

  async upload(file, uploadPath) {
    const fileExt = file.name.split(".").pop();
    const fileName = Math.floor(Date.now()).toString();
    const filePath = `${String(uploadPath).replace(/\/+$/, "") + "/" + fileName}.${fileExt}`;
    return await supabase.storage.from(bucketFor(filePath)).upload(filePath, file);
  },
  async uploadWithProgress(file, uploadPath, onProgress) {
    const cleanFileName = file.name
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9._-]/g, "");

    // no "contracts//123-file.pdf" when the caller's path ends in "/"
    const filePath = `${String(uploadPath).replace(/\/+$/, "")}/${Date.now()}-${cleanFileName}`;

    // Files over 6 MB went through the resumable (TUS) upload, which reported
    // success but never stored the file: every contract / counter-signed
    // contract over 6 MB since April opened as "not found" (5096 Abu Najd,
    // 5130, 5045, 4605 — 29 Oct 2026). The signed-URL upload handles files up
    // to the project's size limit, so it is used for everything up to 50 MB;
    // TUS only above that.
    const USE_TUS = file.size > 50 * 1024 * 1024;

    const bucket = bucketFor(filePath);
    const uploaded = USE_TUS
      ? await this._uploadTus(file, filePath, onProgress, bucket)
      : await this._uploadSigned(file, filePath, onProgress, bucket);

    // Never hand back a path that isn't in storage: the caller saves it on the
    // record, and a missing file only shows up later as a link that won't open.
    if (!(await this.exists(uploaded.path))) {
      throw new Error("The file didn't upload completely. Please try again.");
    }
    return uploaded;
  },

  // Public bucket: the same URL the app opens files with answers 200 when
  // the file is there. Private bucket: a signed link can only be made for a
  // file that exists.
  async exists(path) {
    if (isPrivateDocPath(path)) {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const { data, error } = await supabase.storage.from(PRIVATE_BUCKET).createSignedUrl(toStoragePath(path), 60);
          if (data?.signedUrl) return true;
          if (error && !isNotFound(error)) return true; // the check itself failed: don't block the upload
        } catch (e) {
          return true;
        }
        await new Promise((res) => setTimeout(res, 1500));
      }
      return false;
    }
    const url = storage_url + path;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await fetch(url, { method: "HEAD", cache: "no-store" });
        if (r.ok) return true;
      } catch (e) {
        return true; // network hiccup on the check itself: don't block the upload
      }
      await new Promise((res) => setTimeout(res, 1500));
    }
    return false;
  },

  async _uploadTus(file, filePath, onProgress, bucket = _BUCKET) {
    const { data: { session } } = await supabase.auth.getSession();
    return new Promise((resolve, reject) => {
      const upload = new tus.Upload(file, {
        endpoint: `${process.env.VUE_APP_SUPABASE_URL}/storage/v1/upload/resumable`,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${session.access_token}`,
          apikey: process.env.VUE_APP_SUPABASE_ANON_KEY,
          // No "x-upsert" here. Every path is unique (timestamp prefix), so
          // nothing is ever overwritten, and an upsert makes Storage require a
          // SELECT policy on the bucket, which "brandripplr" doesn't have:
          // every TUS upload (> 50 MB) failed with 403 "new row violates
          // row-level security policy" (29 Oct 2026).
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        // never "resume" an older upload of the same file under its old name
        storeFingerprintForResuming: false,
        chunkSize: 6 * 1024 * 1024, // Supabase requires 6 MB chunks
        metadata: {
          bucketName: bucket,
          objectName: filePath,
          contentType: file.type || "application/octet-stream",
        },
        onProgress(bytesUploaded, bytesTotal) {
          onProgress?.(Number(((bytesUploaded / bytesTotal) * 100).toFixed(2)));
        },
        onError(error) {
          console.error("TUS upload error:", error);
          reject(error);
        },
        onSuccess() {
          onProgress?.(100);
          resolve({ path: filePath });
        },
      });
      upload.start();
    });
  },

  async _uploadSigned(file, filePath, onProgress, bucket = _BUCKET) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUploadUrl(filePath);

    if (error) throw error;

    const { signedUrl } = data;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open("PUT", signedUrl, true);
      xhr.setRequestHeader("apikey", process.env.VUE_APP_SUPABASE_ANON_KEY);
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      xhr.setRequestHeader("x-upsert", "true");

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Number(((e.loaded / e.total) * 100).toFixed(2)));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          resolve({ path: filePath });
        } else {
          console.error("Signed upload failed:", xhr.status, xhr.responseText);
          reject(new Error(xhr.status === 413 ? "The file is too large to upload." : `Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.onabort = () => reject(new Error("Upload cancelled"));

      xhr.send(file);
    });
  },
  async getSignedUrl(path) {
    return await supabase.storage
      .from(bucketFor(path))
      .createSignedUrl(toStoragePath(path), 60 * 60 * 24);
  },

  async uploadPrivate(file, uploadPath) {
    const fileExt = file.name.split(".").pop();
    const fileName = Math.floor(Date.now()).toString();
    const filePath = `${uploadPath + "/" + fileName}.${fileExt}`;
    return await supabase.storage.from(PRIVATE_BUCKET).upload(filePath, file);
  },

  /** 5-minute link to a private document (staff with influencer view/edit permission only). */
  async getPrivateSignedUrl(path, seconds = 300) {
    return await supabase.storage.from(PRIVATE_BUCKET).createSignedUrl(path, seconds);
  },

  /**
   * A link that opens a stored file (path or old public URL).
   * Finance documents / contracts → a short-lived signed link from the private
   * bucket; while a file hasn't been copied there yet (before
   * scripts/move-finance-docs.mjs --copy) it falls back to the public link.
   * Everything else → the public link.
   */
  async fileUrl(value, seconds = 300) {
    if (!value) return null;
    const path = toStoragePath(value);
    if (/^https?:\/\//i.test(path)) return path;
    if (!isPrivateDocPath(path)) return storage_url + path;
    // older contracts sit under ".../contracts//<file>", some under both forms
    const tries = path.includes("//") ? [path, path.replace(/\/{2,}/g, "/")] : [path];
    let lastError = null;
    for (const p of tries) {
      const { data, error } = await supabase.storage.from(PRIVATE_BUCKET).createSignedUrl(p, seconds);
      if (data?.signedUrl) return data.signedUrl;
      lastError = error;
      if (!isNotFound(error)) break;
    }
    if (isNotFound(lastError)) return storage_url + path; // not in the private bucket yet
    throw lastError || new Error("Couldn't open the file.");
  },

  /** Open a stored file in a new tab (see fileUrl). Call from a click handler. */
  async openFile(value) {
    // open the tab synchronously, so the browser doesn't block it as a popup
    const win = window.open("", "_blank");
    try {
      const url = await this.fileUrl(value);
      if (!url) throw new Error("No file.");
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (e) {
      if (win) win.close();
      throw e;
    }
  },

  getPublicUrl(path) {
    return supabase.storage.from(_BUCKET).getPublicUrl(path);
  },
};
